<template>
	<div class="app-time-picker">
		<div
			v-if="$slots.default"
			class="app-time-picker__label"
		>
			<span v-if="required" class="require-mark">*</span>
			<slot></slot>
		</div>

		<div
			class="app-time-picker__input-wrapper"
			:class="{
				'app-time-picker__input-wrapper--disabled' : disabled,
				'app-time-picker__input-wrapper--error' : hasError
			}"
		>
			<input
				class="app-time-picker__input--hidden"
				:required="required"
				:value="hasError ? '' : timeValue"
				@invalid="setInvalidMessage"
			/>

			<AppIcon
				class="app-time-picker__icon"
				icon-name="clock"
				icon-color="#778395"
			/>

			<input
				type="number"
				maxlength="2"
				min="0"
				max="12"
				class="app-time-picker__input"
				placeholder="HH"
				@keypress="keyPressHandler($event)"
				@keydown="hours = keyDownHandler($event)"
				@blur="hours = padStart(hours)"
				@input="hours = inputHandler($event)"
				@paste.prevent
				@invalid="setInvalidMessage"
				:value="hours"
				:disabled="disabled"
				:required="required"
			>
			<div class="app-time-picker__ticks">
				<span class="arrow arrow--up" @click="updateHours(1)"></span>
				<span class="arrow arrow--down" @click="updateHours(-1)"></span>
			</div>

			<span class="app-time-picker__divider">:</span>

			<input
				type="number"
				maxlength="2"
				min="0"
				max="59"
				class="app-time-picker__input"
				placeholder="MM"
				@keypress="keyPressHandler($event)"
				@keydown="minutes = keyDownHandler($event)"
				@blur="minutes = padStart(minutes)"
				@input="minutes = inputHandler($event)"
				@paste.prevent
				@invalid="setInvalidMessage"
				:value="minutes"
				:disabled="disabled"
			>
			<div class="app-time-picker__ticks">
				<span class="arrow arrow--up" @click="updateMinutes(1)"></span>
				<span class="arrow arrow--down" @click="updateMinutes(-1)"></span>
			</div>

			<div class="app-time-picker__toggle">
				<label class="app-time-picker__period-wrapper">
					<input
						type="radio"
						value="AM"
						v-model="period"
						:disabled="disabled"
					>
					<span class="app-time-picker__period">
						AM
					</span>
				</label>
				<label class="app-time-picker__period-wrapper">
					<input
						type="radio"
						value="PM"
						v-model="period"
					>
					<span class="app-time-picker__period">
						PM
					</span>
				</label>
			</div>
		</div>

		<p
			v-if="hasError"
			class="app-time-picker__error"
		>
			{{ error || inputInvalidMessage || invalidMessage}}
		</p>
	</div>
</template>

<script>
export default {
	props: {
		disabled: Boolean,
		required: Boolean,
		error: String,
		value: {
			type: String,
			validator (val) {
				if (val) return new RegExp(/^(?:0?[0-9]|1[0-2])[-:][0-5][0-9]\s*[AP]M|[ap]m$/).test(val)
			}
		},
		min: String,
		max: String
	},

	watch: {
		value: {
			handler (val) {
				if (!val) return this.reset()

				this.hours = this.padStart(val.split(':')[0])
				this.minutes = val.split(':')[1].split(' ')[0]
				this.period = val.split(':')[1].split(' ')[1].toUpperCase()
			},

			immediate: true
		},

		timeValue (val) {
			this.invalidMessage = null
			this.$emit('input', val)
		}
	},

	data () {
		return {
			period: null,
			hours: null,
			minutes: null,
			invalidMessage: null
		}
	},

	computed: {
		hasError () {
			return this.error || this.invalidMessage || this.inputInvalidMessage
		},

		inputInvalidMessage () {
			if (!this.value || this.disabled) return
			const today = new Date().toDateString()
			const selected = new Date(`${today} ${this.timeValue}`).getTime()
			const min = new Date(`${today} ${this.min}`).getTime()
			const max = new Date(`${today} ${this.max}`).getTime()

			if ((this.min && selected < min) ||
				(this.max && selected > max)) return 'Invalid Time'

			return ''
		},

		timeValue () {
			if (this.hours || this.minutes || this.period) {
				const hr = this.padStart(this.hours) || '00'
				const min = this.padStart(this.minutes)	 || '00'
				const per = this.period	 || 'AM'

				return `${hr}:${min} ${per}`
			}

			return null
		}
	},

	methods: {
		keyPressHandler (event) {
			const code = event.which || event.keyCode
			if (code > 57 || code < 48) event.preventDefault()
		},

		keyDownHandler (event) {
			const code = event.which || event.keyCode

			if (code === 38 || code === 40) {
				event.preventDefault()
				const initial = Number(event.target.value)
				const max = Number(event.target.max)
				const min = Number(event.target.min)
				let step = 0
				if (code === 38) step = 1
				if (code === 40) step = -1
				return this.tick(initial, step, min, max)
			}

			return event.target.value
		},

		inputHandler (event) {
			if (!event.target.value) return null
			const value = Number(event.target.value)
			const max = Number(event.target.max)
			const min = Number(event.target.min)
			// return Math.min(Math.max(value, min), max).toString()
			if (value > max) return this.padStart(min)
			if (value < min) return this.padStart(max)
			return this.padStart(value).toString()
		},

		padStart (value) {
			if (value === '' || value === null) return null
			return String(value).padStart(2, 0)
		},

		updateHours (step) {
			this.hours = this.tick(this.hours, step, 0, 12)
		},

		updateMinutes (step) {
			this.minutes = this.tick(this.minutes, step, 0, 59)
		},

		tick (initial, step, min, max) {
			const value = Number(initial) + Number(step)
			// return this.padStart(Math.min(Math.max(value, min), max))
			if (value > max) return this.padStart(min)
			if (value < min) return this.padStart(max)
			return this.padStart(value)
		},

		reset () {
			this.period = null
			this.hours = null
			this.minutes = null
		},

		setInvalidMessage (event) {
			event.preventDefault()
			if (event.target.validity.valueMissing) {
				this.invalidMessage = this.$t('error_fill_up_field')
			} else {
				this.invalidMessage = this.$t('error_invalid_input')
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.app-time-picker {
	display: inline-block;
	position: relative;

	&__label {
		font-size: 1em;
		margin-bottom: .5em;

		.require-mark {
			color: $danger;
		}
	}

	&__input-wrapper {
		display: flex;
		align-items: center;
		padding: 5px 8px;
		border: 1px solid $default-light;
		border-radius: 3px;
		color: $default;

		&:hover, &:focus-within {
			border-color: $primary;
		}

		&--error,
		&--error:hover,
		&--error:focus-within {
			border-color: $danger;
		}
	}

	&__icon {
		width: 1em;
		height: 1em;
		margin-right: .5em;
	}

	&__input {
		line-height: 1em;
		font-size: 1em;
		width: 1.5em;
		text-align: center;
		border: none;
		outline: none;
		box-shadow: 0 .5px 0 $default-light;
		color: $default;
		caret-color: $primary;
		background-color: #fff;

		&::placeholder {
			color: $default-light;
			font-size: .8em;
			line-height: 1em;
		}

		&:focus {
			box-shadow: 0 .5px 0 $primary;
			caret-color: $primary;

			&::placeholder {
				color: transparent;
			}
		}

		/* Chrome, Safari, Edge, Opera */
		&::-webkit-outer-spin-button,
		&::-webkit-inner-spin-button {
			-webkit-appearance: none;
			margin: 0;
		}

		/* Firefox */
		-moz-appearance: textfield;

		&--hidden {
			position: absolute;
			top: 0;
			left: 0;
			width: 0;
			height: 0;
			opacity: 0;
			z-index: 0;
			pointer-events: none;
		}
	}

	&__ticks {
		display: flex;
		flex-direction: column;
		position: relative;
		height: 1em;
		width: 1em;
		margin: 0 .5em;

		.arrow {
			display: inline-block;
			cursor: pointer;
			width: .4em;
			height: 0;
			border: .4em solid transparent;
			margin: 1px 0;
			position: absolute;
			left: 0;

			&--up {
				border-bottom-color: $default;
				bottom: .5em;

				&:hover {
					border-bottom-color: $primary;
				}
			}

			&--down {
				border-top-color: $default;
				top: .5em;

				&:hover {
					border-top-color: $primary;
				}
			}
		}
	}

	&__divider {
		display: inline-block;
		text-align: center;
		margin-right: .5em;
	}

	&__toggle {
		display: flex;
		border-radius: 3px;
		overflow: hidden;
	}

	&__period-wrapper {
		position: relative;
		cursor: pointer;

		input {
			appearance: none;
			position: absolute;
			z-index: 0;
			opacity: 0;
			outline: none;
			pointer-events: none;
			background-color: #fff;
		}
	}

	&__period {
		height: 100%;
		padding: .2em .5em;
		border: none;
		background-color: $primary-light;
		color: $primary;
		font-size: .8em;
		line-height: 1em;
	}

	input:checked ~ &__period {
		background-color: $primary;
		color: #fff;
	}

	&__input-wrapper--disabled {
		pointer-events: none;
		opacity: 0.5;
	}

	&__input-wrapper--disabled &__period {
		background-color: $default-light;
		color: $default;
	}

	&__error {
		color: $danger;
		margin-top: .5em;
		display: flex;
		justify-content: flex-end;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/app-components/AppTimePicker.vue